import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Eye, EyeOff, PlayCircle, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const AREAS = ['九龍', '港島', '新界'];

const emptyForm = {
  name: '',
  area: '九龍',
  routes: [],
  published: true,
  sort_order: 0,
};

const AdminExamRoutes = () => {
  const [examRoutes, setExamRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingRoute, setEditingRoute] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchExamRoutes();
  }, []);

  const fetchExamRoutes = async () => {
    try {
      const response = await axios.get(`${API_URL}/exam-routes?published_only=false`);
      setExamRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch exam routes:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRoute) {
        await axios.put(`${API_URL}/exam-routes/${editingRoute.id}`, formData);
        toast.success('更新成功');
      } else {
        await axios.post(`${API_URL}/exam-routes`, formData);
        toast.success('新增成功');
      }
      setDialogOpen(false);
      resetForm();
      fetchExamRoutes();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/exam-routes/${deleteTarget}`);
      toast.success('刪除成功');
      fetchExamRoutes();
    } catch (error) {
      toast.error('刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const togglePublish = async (route) => {
    try {
      await axios.put(`${API_URL}/exam-routes/${route.id}`, { published: !route.published });
      toast.success(route.published ? '已隱藏' : '已發布');
      fetchExamRoutes();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleMove = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= examRoutes.length) return;
    const reordered = [...examRoutes];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setExamRoutes(reordered);
    try {
      const payload = reordered.map((r, i) => ({ id: r.id, sort_order: i }));
      await axios.post(`${API_URL}/exam-routes/reorder`, payload);
      toast.success('次序已更新');
    } catch (error) {
      toast.error('排序失敗');
      fetchExamRoutes();
    }
  };

  const openDialog = (route = null) => {
    if (route) {
      setEditingRoute(route);
      setFormData({
        name: route.name,
        area: route.area,
        routes: route.routes || [],
        published: route.published,
        sort_order: route.sort_order || 0,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRoute(null);
    setFormData(emptyForm);
  };

  // Video rows management
  const addVideoRow = () => {
    setFormData({ ...formData, routes: [...formData.routes, { label: '', videoId: '' }] });
  };

  const updateVideoRow = (index, field, value) => {
    const updated = formData.routes.map((r, i) =>
      i === index ? { ...r, [field]: value } : r
    );
    setFormData({ ...formData, routes: updated });
  };

  const removeVideoRow = (index) => {
    setFormData({ ...formData, routes: formData.routes.filter((_, i) => i !== index) });
  };

  const areaColor = (area) => {
    if (area === '九龍') return 'bg-blue-100 text-blue-700';
    if (area === '港島') return 'bg-purple-100 text-purple-700';
    if (area === '新界') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">考試攻略管理</h1>
            <p className="text-muted-foreground mt-1">管理各考場路線及 YouTube 影片</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                新增考場
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingRoute ? '編輯考場路線' : '新增考場路線'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-5 pt-2">
                {/* Name + Area */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>考場名稱 *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="例：永孝街"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>地區 *</Label>
                    <select
                      value={formData.area}
                      onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      required
                    >
                      {AREAS.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Sort order */}
                <div className="space-y-2">
                  <Label>排列順序（數字越小越前）</Label>
                  <Input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-32"
                  />
                </div>

                {/* Published */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="published">公開顯示</Label>
                </div>

                {/* Video routes */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>路線影片</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVideoRow}>
                      <Plus className="h-3 w-3 mr-1" />
                      加路線
                    </Button>
                  </div>
                  {formData.routes.length === 0 && (
                    <p className="text-sm text-muted-foreground bg-gray-50 rounded-lg p-3 text-center">
                      未有影片（考場仍會顯示，但沒有播放按鈕）
                    </p>
                  )}
                  {formData.routes.map((row, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <Input
                        value={row.label}
                        onChange={(e) => updateVideoRow(index, 'label', e.target.value)}
                        placeholder="路線名（例：路線1）"
                        className="w-32 flex-shrink-0"
                      />
                      <Input
                        value={row.videoId}
                        onChange={(e) => updateVideoRow(index, 'videoId', e.target.value)}
                        placeholder="YouTube 影片 ID（例：jgyvYOD32Q0）"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeVideoRow(index)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {formData.routes.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      YouTube ID 從網址取得，例如 youtube.com/watch?v=<strong>jgyvYOD32Q0</strong>
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    取消
                  </Button>
                  <Button type="submit">
                    {editingRoute ? '儲存更新' : '新增考場'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {AREAS.map((area) => {
            const count = examRoutes.filter((r) => r.area === area).length;
            return (
              <div key={area} className="bg-white rounded-xl border p-4 text-center">
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-sm text-muted-foreground">{area}</div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">載入中...</div>
        ) : examRoutes.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <PlayCircle className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-muted-foreground">尚未新增任何考場路線</p>
            <Button className="mt-4" onClick={() => openDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              新增第一個考場
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full min-w-[480px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600 w-28">次序</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">考場</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">地區</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">路線數</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">狀態</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">操作</th>
                </tr>
              </thead>
              <tbody>
                {examRoutes.map((route, index) => (
                  <tr key={route.id} className={`border-b last:border-0 hover:bg-gray-50 ${!route.published ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMove(index, -1)}
                          disabled={index === 0}
                          title="上移"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMove(index, 1)}
                          disabled={index === examRoutes.length - 1}
                          title="下移"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold">{route.name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${areaColor(route.area)}`}>
                        {route.area}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {route.routes && route.routes.length > 0 ? (
                          <>
                            <PlayCircle className="h-4 w-4 text-green-500" />
                            <span className="text-sm">{route.routes.length} 條路線</span>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">未有影片</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        route.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {route.published ? '公開' : '隱藏'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => togglePublish(route)}
                          title={route.published ? '隱藏' : '發布'}
                        >
                          {route.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDialog(route)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => setDeleteTarget(route.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除此考場路線？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，考場路線將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminExamRoutes;
